import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import {
  canAdminister,
  canBill,
  canHandleRequests,
  canViewClients,
  peutSaisirTemps,
  peutVoirTableauDeBord,
  type AccountKind,
  type Role,
} from '@its/shared';
import { useNouvelleDemande } from '@/composables/useNouvelleDemande';

/**
 * Les routes de l'interface.
 *
 * La garde AMÉLIORE le confort — elle évite d'afficher un écran vide — mais ne
 * protège rien : c'est l'API qui refuse. Ne jamais compter sur elle pour la
 * sécurité.
 */
declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    public?: boolean;
    /** Règle de `@its/shared` qui ouvre l'écran ; sans elle, tout compte connecté. */
    peut?: (roles: readonly Role[], kind: AccountKind | null) => boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { title: 'Connexion', public: true },
  },
  {
    path: '/connexion/retour',
    name: 'retour-connexion',
    component: () => import('@/pages/RetourConnexionPage.vue'),
    meta: { title: 'Connexion', public: true },
  },
  {
    path: '/activer',
    name: 'activer',
    component: () => import('@/pages/ActivationPage.vue'),
    meta: { title: 'Activer mon accès', public: true },
  },
  {
    // Le lien du courriel « Mot de passe oublié » (DEC-041) : jeton dans le fragment.
    path: '/reinitialiser-mot-de-passe',
    name: 'reinitialiser-mot-de-passe',
    component: () => import('@/pages/ReinitialisationPage.vue'),
    meta: { title: 'Choisir mon mot de passe', public: true },
  },
  {
    path: '/temps',
    name: 'temps',
    component: () => import('@/pages/FeuilleTempsPage.vue'),
    meta: { title: 'Ma feuille de temps', peut: peutSaisirTemps },
  },
  {
    path: '/interventions',
    name: 'interventions',
    component: () => import('@/pages/InterventionsPage.vue'),
    meta: { title: 'Interventions', peut: canViewClients },
  },
  {
    path: '/prefacturation',
    name: 'prefacturation',
    component: () => import('@/pages/PrefacturationPage.vue'),
    meta: { title: 'Pré-facturation', peut: canBill },
  },
  {
    path: '/notes-facturation',
    name: 'notes-facturation',
    component: () => import('@/pages/NotesFacturationPage.vue'),
    meta: { title: 'Notes de facturation', peut: canBill },
  },
  {
    path: '/clients',
    name: 'clients',
    component: () => import('@/pages/ClientsPage.vue'),
    meta: { title: 'Clients', peut: canViewClients },
  },
  {
    path: '/clients/:id',
    name: 'client',
    component: () => import('@/pages/ClientPage.vue'),
    meta: { title: 'Client', peut: canViewClients },
  },
  {
    // L'accueil dépend de qui l'on est (DEC-028) : le staff arrive sur le
    // tableau de bord de l'équipe, tous les autres sur leurs demandes — ce
    // qu'ils viennent chercher en ouvrant la borne. Le chemin `/` reste la
    // destination de repli des gardes et du retour de connexion. Une garde
    // d'entrée et non un `redirect` : celui-ci se résout AVANT que la garde
    // globale ait chargé le compte.
    path: '/',
    name: 'accueil',
    component: { render: () => null },
    beforeEnter: async () => {
      const { useAuthStore } = await import('@/stores/auth');
      return peutVoirTableauDeBord(useAuthStore().user?.kind) ? { path: '/tableau-de-bord' } : { path: '/mes-demandes' };
    },
  },
  {
    path: '/tableau-de-bord',
    name: 'tableau-de-bord',
    component: () => import('@/pages/TableauDeBordPage.vue'),
    meta: { title: 'Tableau de bord', peut: (_roles, kind) => peutVoirTableauDeBord(kind) },
  },
  {
    path: '/mes-demandes',
    name: 'mes-demandes',
    component: () => import('@/pages/MesDemandesPage.vue'),
    meta: { title: 'Mes demandes' },
  },
  {
    path: '/file',
    name: 'file',
    component: () => import('@/pages/FileDemandesPage.vue'),
    meta: { title: 'File des demandes', peut: canHandleRequests },
  },
  {
    path: '/periodiques',
    name: 'periodiques',
    component: () => import('@/pages/PeriodiquesPage.vue'),
    meta: { title: 'Demandes périodiques', peut: canHandleRequests },
  },
  {
    // Pas un écran : une modale, ouverte par-dessus l'écran en cours
    // (useNouvelleDemande). L'adresse ouvre la modale et ANNULE la
    // navigation — on reste où l'on était. Arrivé par un lien direct, sans
    // écran derrière, on pose la modale sur « Mes demandes ».
    path: '/nouvelle-demande',
    name: 'nouvelle-demande',
    component: { render: () => null },
    beforeEnter: (_to, from) => {
      useNouvelleDemande().ouvrir();
      return from.matched.length ? false : { path: '/mes-demandes' };
    },
  },
  {
    path: '/demandes/:id',
    name: 'demande',
    component: () => import('@/pages/DemandePage.vue'),
    meta: { title: 'Demande' },
  },
  {
    path: '/administration/comptes',
    name: 'comptes',
    component: () => import('@/pages/ComptesPage.vue'),
    meta: { title: 'Comptes et rôles', peut: canAdminister },
  },
  {
    path: '/administration/types-intervention',
    name: 'types-intervention',
    component: () => import('@/pages/TypesInterventionPage.vue'),
    meta: { title: 'Types d’intervention', peut: canAdminister },
  },
  {
    path: '/decouverte',
    name: 'decouverte',
    component: () => import('@/pages/DecouvertePage.vue'),
    meta: { title: 'Visite guidée' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'introuvable',
    component: () => import('@/pages/IntrouvablePage.vue'),
    meta: { title: 'Page introuvable' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const { useAuthStore } = await import('@/stores/auth');
  const auth = useAuthStore();
  if (!auth.ready) await auth.init();

  if (to.meta.public) {
    // Le retour de Microsoft doit passer même connecté : il porte la session.
    if (to.name === 'login' && auth.isAuthenticated) return { path: '/' };
    return true;
  }
  if (!auth.isAuthenticated) return { name: 'login', query: { redirect: to.fullPath } };
  if (to.meta.peut && !to.meta.peut(auth.roles, auth.user?.kind ?? null)) return { path: '/' };
  return true;
});

router.afterEach((to, _from, echec) => {
  // Une navigation annulée (la nouvelle demande, qui ouvre une modale) laisse l'écran — et son titre — en place.
  if (echec) return;
  document.title = to.meta.title ? `${to.meta.title} — Borne technique` : 'Borne technique — IT Solutions';
});
